import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  TextField,
  Box,
} from '@mui/material';
import XIcon from '@mui/icons-material/X';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// @ts-ignore
import HubspotForm from 'react-hubspot-form';

interface ShareResultsDialogProps {
  open: boolean;
  onClose: () => void;
  winner: string;
  score: string;
  topic: string;
}

const ShareResultsDialog: React.FC<ShareResultsDialogProps> = ({
  open,
  onClose,
  winner,
  score,
  topic,
}) => {
  const [tweetText, setTweetText] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    const isTie = score === '50-50';
    const defaultText = isTie
      ? `I just tied in an argument about ${topic} on Impartial! Use Impartial to settle disputes fairly.`
      : `${winner} won an argument about ${topic} ${score} using Impartial! Use Impartial to settle disputes fairly.`;
    setTweetText(defaultText);
  }, [winner, score, topic]);

  useEffect(() => {
    // URL will take ~23 characters after t.co shortening
    setCharCount(tweetText.length + 23);
  }, [tweetText]);

  const handleShare = () => {
    const url = 'https://beimpartial.ai';
    const encodedText = encodeURIComponent(tweetText);
    const encodedUrl = encodeURIComponent(url);
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
  };

  const handleClose = () => {
    setShowEmailForm(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 0,
        },
      }}
    >
      <DialogContent 
        sx={{ 
          p: 3,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Toggle between email form view and share options view */}
        {showEmailForm ? (
          // EMAIL FORM VIEW - Only shown after clicking "Email Results"
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setShowEmailForm(false)}
                sx={{ mr: 2 }}
              >
                Back
              </Button>
              <Typography variant="h2">
                Email Results
              </Typography>
            </Box>
            <HubspotForm
              portalId="122163"
              formId="e6e67273-3361-4fc1-9d8d-408b0c33a14a"
              onSubmit={() => console.log('Form submitted!')}
              onReady={() => console.log('Form ready!')}
              loading={<Typography>Loading...</Typography>}
            />
          </>
        ) : (
          // INITIAL SHARE OPTIONS VIEW - Shows both sharing options
          <>
            <Typography variant="h2" gutterBottom>
              Share Results
            </Typography>

            <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
              Share the results of this argument!
            </Typography>

            {/* Twitter sharing option */}
            <TextField
              multiline
              rows={4}
              value={tweetText}
              onChange={(e) => setTweetText(e.target.value)}
              fullWidth
              variant="outlined"
            />

            <Typography variant="body2" color="text.primary" sx={{ mt: 1, mb: 2 }}>
              {charCount}/280 characters
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={handleShare}
              startIcon={<XIcon />}
              color="primary"
              sx={{ mb: 2 }}
            >
              Share on X
            </Button>

            {/* Email sharing option */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => setShowEmailForm(true)}
              startIcon={<EmailIcon />}
              color="primary"
              sx={{ mb: 2 }}
            >
              Email Results
            </Button>
          </>
        )}

        {/* Cancel button always visible */}
        <Button
          variant="text"
          fullWidth
          onClick={handleClose}
          sx={{
            py: 1.5,
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ShareResultsDialog; 